import i18next from 'i18next';
import React, {Suspense, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {DatePicker} from '@famly/mf_date-and-time_date-picker';
import {Button} from '@famly/mf_input_button';
import {Checkbox} from '@famly/mf_input_checkbox';
import {FormControl} from '@famly/mf_input_form-control';
import {MultiSelect} from '@famly/mf_input_select';
import {Notice} from '@famly/mf_data-display_notice';
import {Select} from '@famly/mf_input_select';
import {Text} from '@famly/mf_data-display_text';
import {createStack, Stack} from '@famly/mf_layout_stack';
import {Box, createBox} from '@famly/mf_layout_box';
import {TARGET_IDS} from 'web-app/services/intercom-constants';
import {head, isNonEmpty} from 'web-app/util/non-empty-array';
import {getCurrency} from 'web-app/util/numeral';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {CollapsibleContainer} from './components/collapsible-container';
import {Fees} from './components/fees';
import {OptionAdornment} from './components/option-adornment';
import PricingGroupSelect from './components/pricing-group-select';
import {Summary} from './components/summary';
import {Tuitions} from './components/tuitions';
import {useSummaryPeriods} from './hooks/use-summary-periods';
import {getStartDateForEndPeriod} from './transformers/end-date';
import {transformTuitionsOrFeesToProratedItem} from './transformers/prorated-item-transformer';
import {transformBillingPeriodsToBillingPeriodOptions} from './transformers/select-options-transformers';
import type * as Types from './types';
import Spinner from '../components/loading/spinner/spinner';
import {biweeklyBillingStartDate} from './helpers/create-billing-periods';
import {BillingPeriodSelect} from './components/billing-period-select';
import {toLowerCaseWithoutWhitespaces} from './helpers/helpers';
import {FallbackButtonToLegacyPlanEditor} from './components/fallback-button-to-legacy-plan-editor';
import {isCreate, hasFuturePlansForCreate} from './helpers/state-projections';

const CustomPeriodModal = React.lazy(() =>
    import('./components/custom-period-modal').then(mod => ({default: mod.CustomPeriodModal})),
);
const EndPeriodModal = React.lazy(() =>
    import('./components/end-period-modal').then(mod => ({default: mod.EndPeriodModal})),
);

// We experience issues with the modal height.
// If we don't subtract the modal header height,
// The columns of the grid container are not taking up the entire screen
// To solve this, we couple the constants with the actual height, padding and border-width values of the components.
// In this way, even if we change those values, the layout will still work as intended.

// All the calculations are needed to make the CTA buttons always stay on the bottom of the screen
// As well as to create scrollable container in the remaining space of the right zone

const MODAL_TOP_BAR_HEIGHT = 80;

// Value of padding of the right zone which contains CTA buttons
const CONTAINER_PADDING = 10;

// Grid component has border top which we also need to subtract
const BORDER_WIDTH = 1;

// In Modern Famly the value 1 equals 4px when it comes to paddings and margins
const DEFAULT_PADDING_VALUE = 4;

// We multiple by 2 at the end so we get both top and bottom padding value in px
const TOP_AND_BOTTOM_PADDING = CONTAINER_PADDING * DEFAULT_PADDING_VALUE * 2;

const Grid = createBox({
    display: 'grid',
    gridTemplateColumns: '60% 1fr',
    width: '100%',
    height: '100%',
});

const StackWithoutScrollbar = styled(
    createStack({
        direction: 'column',
        spacing: 8,
        p: 10,
        backgroundColor: 'n0',
        overflow: 'auto',
    }),
)`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */

    scrollbar-width: none; /* Firefox */
`;

const Form = styled.form`
    height: calc(100% - ${MODAL_TOP_BAR_HEIGHT + BORDER_WIDTH}px);
`;

export type Props = {
    handleBillingFrequencyChange: Types.HandleBillingFrequencyChange;
    handlePricingGroupChange: Types.HandlePricingGroupChange;
    handleDiscountChange: Types.HandleDiscountChange;
    handleToggleMoreOptions: Types.HandleToggleMoreOptions;
    handleEndDateChange: Types.HandleEndDateChange;
    handleBillingPeriodChange: Types.HandleBillingPeriodChange;
    handleCustomEndPeriodProratedItemUpdate: Types.HandleCustomEndPeriodProratedItemUpdate;
    handleCustomEndPeriodSave: Types.HandleCustomEndPeriodSave;
    handleCustomEndPeriodCancel: Types.HandleCustomEndPeriodCancel;
    handleChildrenChange: Types.HandleChildrenChange;
    handleTuitionsChange: Types.HandleTuitionsChange;
    appendTuitionField: Types.AppendTuitionField;
    removeTuitionField: Types.RemoveTuitionField;
    toggleTuitionFieldAtIndex: Types.ToggleTuitionField;
    toggleFeeFieldAtIndex: Types.ToggleFeeField;
    handleFeesChange: Types.HandleFeesChange;
    appendFeeField: Types.AppendFeeField;
    removeFeeField: Types.RemoveFeeField;
    handleCustomStartPeriodChange: Types.HandleCustomStartPeriodChange;
    handleCustomStartPeriodProratedItemUpdate: Types.HandleCustomStartPeriodProratedItemUpdate;
    handleCustomStartPeriodSave: Types.HandleCustomStartPeriodSave;
    handleModalToggle: Types.HandleModalToggle;
    handleModalClose: Types.HandleModalClose;
    onClose: VoidFunction;
    saveButton: {
        onSave: VoidFunction;
        isDisabled: boolean;
        isLoading: boolean;
    };
    cancelButton: {
        onCancel: VoidFunction;
    };
    childrenOptions: Array<Types.ChildrenOption>;
    feeOptions: Array<Types.TuitionAndFeeOption>;
    pricingGroupOptions: Array<Types.PricingGroupOption>;
    billingFrequencyOptions: Array<Types.BillingFrequencyOption>;
    state: Types.State;
    tuitionOptions: Array<Types.TuitionAndFeeOption>;
    discountOptions: Array<Types.DiscountOption>;
    canOverrideTuitionPrice: boolean;
    billingFrequencies: Array<Types.BillingFrequency>;
    handleAddNewTemplateChange: Types.HandleAddNewTemplateChange;
    handleRemoveChildrenWithError: Types.HandleRemoveChildrenWithError;
    handleLegacyPlanModalOpen: VoidFunction;
};

export const SimplifiedUSBillingPlanModal = ({
    handleBillingFrequencyChange,
    handlePricingGroupChange,
    handleToggleMoreOptions,
    handleDiscountChange,
    handleEndDateChange,
    handleBillingPeriodChange,
    handleCustomEndPeriodProratedItemUpdate,
    handleCustomEndPeriodSave,
    handleCustomEndPeriodCancel,
    handleChildrenChange,
    handleTuitionsChange,
    appendTuitionField,
    removeTuitionField,
    toggleTuitionFieldAtIndex,
    toggleFeeFieldAtIndex,
    handleFeesChange,
    appendFeeField,
    removeFeeField,
    handleCustomStartPeriodChange,
    handleCustomStartPeriodProratedItemUpdate,
    handleCustomStartPeriodSave,
    handleModalToggle,
    handleModalClose,
    handleAddNewTemplateChange,
    onClose,
    handleLegacyPlanModalOpen,
    saveButton,
    cancelButton,
    childrenOptions,
    feeOptions,
    pricingGroupOptions,
    state,
    tuitionOptions,
    discountOptions,
    billingFrequencyOptions,
    canOverrideTuitionPrice,
    billingFrequencies,
    handleRemoveChildrenWithError,
}: Props) => {
    const {
        childrenState: {selectedChildren, allSelectedChildrenHaveBillPayer},
        tuitionsState: {selectedTuitions, allSelectedTuitionsHaveTheSameFrequency},
        billingPeriodState: {selectedBillingPeriod},
        billingFrequencyState: {selectedBillingFrequency},
        selectedBillingType,
        customPeriodState,
        feesState: {selectedFees, fieldCurrentlyOpen},
        billingPeriodState,
        showMoreOptions,
        pricingGroupState: {selectedPricingGroup, placement: pricingGroupSelectPlacement, hasAutomaticPricingGroup},
        discountState: {selectedDiscounts},
        endDateState: {selectedEndDate, error: endDateError},
        displayedModal,
        template: {shouldAddNewTemplate},
        isAutomaticDecouplingEnabled,
        childrenErrors,
    } = state;

    const hasMatchingBillingType = useCallback(
        (): boolean => isNonEmpty(selectedTuitions) && selectedBillingType === head(selectedTuitions).type,
        [selectedBillingType, selectedTuitions],
    );

    const canCalculateInvoiceAmount = useMemo(
        () =>
            selectedBillingFrequency?.type === 'none' ||
            (allSelectedTuitionsHaveTheSameFrequency && hasMatchingBillingType()),
        [allSelectedTuitionsHaveTheSameFrequency, hasMatchingBillingType, selectedBillingFrequency],
    );

    const hasFuturePlans = useMemo(() => isCreate(state) && hasFuturePlansForCreate(state), [state]);

    const summaryPeriods = useSummaryPeriods({
        selectedBillingType,
        selectedBillingPeriod,
        selectedTuitions,
        selectedFees,
        selectedDiscounts,
        selectedEndDate,
        selectedStartDate: customPeriodState.customPeriodDates?.startDate ?? null,
        selectedPricingGroup,
        selectedChildren,
        pricingGroupOptions,
        selectedBillingFrequency,
        isEditView: false,
        biweeklyBillingPeriodStartDate: biweeklyBillingStartDate({billingFrequencies}),
        showOnePeriod: hasFuturePlans,
    });

    const billingPeriodOptions = useMemo(() => {
        switch (selectedBillingFrequency?.type) {
            case 'biweekly':
                return [
                    ...transformBillingPeriodsToBillingPeriodOptions(billingPeriodState.biweekly, customPeriodState),
                ];
            case 'weekly':
                return [...transformBillingPeriodsToBillingPeriodOptions(billingPeriodState.weekly, customPeriodState)];
            case 'monthly':
                return [
                    ...transformBillingPeriodsToBillingPeriodOptions(billingPeriodState.monthly, customPeriodState),
                ];
            default: {
                return [];
            }
        }
    }, [billingPeriodState, customPeriodState, selectedBillingFrequency?.type]);

    const proratedItemsForStartCustomPeriod = useMemo(() => {
        const items = [...selectedTuitions, ...selectedFees];
        if (!isNonEmpty(items)) {
            return null;
        }

        return transformTuitionsOrFeesToProratedItem(
            'start',
            customPeriodState.customPeriodDates,
            items,
            selectedPricingGroup,
        );
    }, [customPeriodState.customPeriodDates, selectedFees, selectedPricingGroup, selectedTuitions]);

    const startDateOfEndPeriod = useMemo(
        () =>
            getStartDateForEndPeriod(
                selectedBillingFrequency,
                selectedEndDate,
                biweeklyBillingStartDate({billingFrequencies}),
            ),
        [billingFrequencies, selectedBillingFrequency, selectedEndDate],
    );

    const proratedItemsForEndCustomPeriod = useMemo(() => {
        const items = [...selectedTuitions, ...selectedFees];
        if (!isNonEmpty(items) || !hasValue(selectedEndDate) || !hasValue(startDateOfEndPeriod)) {
            return null;
        }

        return transformTuitionsOrFeesToProratedItem(
            'end',
            {
                startDate: startDateOfEndPeriod,
                endDate: selectedEndDate,
            },
            items,
            selectedPricingGroup,
        );
    }, [selectedEndDate, selectedFees, selectedPricingGroup, selectedTuitions, startDateOfEndPeriod]);

    return (
        <>
            <Stack
                justifyContent="space-between"
                px={6}
                alignItems="center"
                data-e2e-id="simplified-billing-modal"
                height={MODAL_TOP_BAR_HEIGHT}
            >
                <Text variant="h6" emphasized data-intercom-target={TARGET_IDS.NEW_BILLING_PLAN_HEADER}>
                    {i18next.t('react.simplifiedUsBillingPlan.translations.json.newBillingPlan')}
                </Text>
                <Stack spacing={4}>
                    <Button
                        onClick={onClose}
                        variant="transparent"
                        aria-label={i18next.t('react.simplifiedUsBillingPlan.translations.json.close')}
                        size="regular"
                        icon="close"
                    />
                </Stack>
            </Stack>
            <Form
                onSubmit={event => {
                    event.preventDefault();
                    saveButton.onSave();
                }}
            >
                <Grid borderTop="n100" borderTopWidth={BORDER_WIDTH}>
                    <StackWithoutScrollbar>
                        <FormControl
                            fullWidth
                            label={i18next.t('react.simplifiedUsBillingPlan.translations.json.children')}
                        >
                            <MultiSelect
                                onChange={handleChildrenChange}
                                options={childrenOptions}
                                value={selectedChildren.map(child => child.id)}
                                data-e2e-id="children-multi-select"
                            />
                        </FormControl>
                        {!allSelectedChildrenHaveBillPayer ? (
                            <Box mt={4}>
                                <Notice
                                    severity="error"
                                    title={i18next.t(
                                        'react.simplifiedUsBillingPlan.translations.json.noBillPayerNotice.title',
                                    )}
                                    text={i18next.t(
                                        'react.simplifiedUsBillingPlan.translations.json.noBillPayerNotice.description',
                                    )}
                                />
                            </Box>
                        ) : null}
                        {pricingGroupSelectPlacement === 'outside-more-options' ? (
                            <PricingGroupSelect
                                options={pricingGroupOptions}
                                value={selectedPricingGroup}
                                onChange={handlePricingGroupChange}
                            />
                        ) : null}
                        <Tuitions
                            onChange={handleTuitionsChange}
                            appendField={appendTuitionField}
                            removeField={removeTuitionField}
                            toggleField={toggleTuitionFieldAtIndex}
                            tuitionsState={state.tuitionsState}
                            tuitionOptions={tuitionOptions}
                            isDisabled={!hasAutomaticPricingGroup && !hasValue(selectedPricingGroup)}
                            canOverridePrice={canOverrideTuitionPrice}
                            handleModalToggle={handleModalToggle}
                        />
                        <FormControl fullWidth label={needsTranslations('SimplifiedUsBilling', 'Discount (optional)')}>
                            <Stack alignItems="center">
                                <MultiSelect
                                    fullWidth
                                    onChange={handleDiscountChange}
                                    options={discountOptions}
                                    value={selectedDiscounts.map(discount => discount.id)}
                                    getOptionAdornment={option => (
                                        <OptionAdornment>{option.type === 'discount' && option.amount}</OptionAdornment>
                                    )}
                                    data-e2e-id="discounts-select"
                                />
                            </Stack>
                        </FormControl>
                        <Stack>
                            <Box width="100%" mr={5}>
                                <FormControl
                                    fullWidth
                                    label={
                                        isAutomaticDecouplingEnabled
                                            ? i18next.t(
                                                  'react.simplifiedUsBillingPlan.translations.json.billingFrequency',
                                              )
                                            : i18next.t(
                                                  'react.simplifiedUsBillingPlan.translations.json.automaticBilling',
                                              )
                                    }
                                >
                                    <Select
                                        onChange={handleBillingFrequencyChange}
                                        options={billingFrequencyOptions}
                                        value={selectedBillingFrequency}
                                        data-e2e-id="billing-frequency-select"
                                    />
                                </FormControl>
                            </Box>
                            <BillingPeriodSelect
                                onChange={handleBillingPeriodChange}
                                editCustomPeriod={() => handleModalToggle({type: 'startPeriod'})}
                                selectedBillingPeriod={selectedBillingPeriod}
                                isDisabled={!isNonEmpty(selectedTuitions)}
                                options={billingPeriodOptions}
                            />
                        </Stack>
                        <CollapsibleContainer
                            isOpen={showMoreOptions}
                            onToggle={() => handleToggleMoreOptions(showMoreOptions)}
                        >
                            {pricingGroupSelectPlacement === 'inside-more-options' ? (
                                <PricingGroupSelect
                                    options={pricingGroupOptions}
                                    value={selectedPricingGroup}
                                    onChange={handlePricingGroupChange}
                                />
                            ) : null}
                            <Fees
                                onChange={handleFeesChange}
                                appendField={{
                                    onClick: appendFeeField,
                                    isDisabled: !hasAutomaticPricingGroup && !hasValue(selectedPricingGroup),
                                }}
                                removeField={removeFeeField}
                                fields={state.feesState.inputFields}
                                feeOptions={feeOptions}
                                toggleField={toggleFeeFieldAtIndex}
                                fieldCurrentlyOpen={fieldCurrentlyOpen}
                            />
                            <FormControl
                                fullWidth
                                label={needsTranslations('SimplifiedUsBilling', 'End date (optional)')}
                                errorMessage={endDateError ?? undefined}
                            >
                                <DatePicker
                                    disabled={!selectedBillingPeriod || hasFuturePlans}
                                    fullWidth
                                    value={selectedEndDate}
                                    onAccept={option =>
                                        handleEndDateChange(
                                            option,
                                            selectedBillingFrequency?.type ?? null,
                                            biweeklyBillingStartDate({billingFrequencies}),
                                        )
                                    }
                                    minDate={selectedBillingPeriod?.unformattedStartDate}
                                    data-e2e-id="end-date-picker"
                                />
                            </FormControl>
                            {selectedChildren.length === 1 ? (
                                <Stack>
                                    <FallbackButtonToLegacyPlanEditor
                                        type="link"
                                        onClick={() => {
                                            handleLegacyPlanModalOpen();
                                            onClose();
                                        }}
                                    />
                                </Stack>
                            ) : null}
                        </CollapsibleContainer>
                    </StackWithoutScrollbar>
                    <Stack
                        p={CONTAINER_PADDING}
                        backgroundColor="n50"
                        borderLeft="n100"
                        direction="column"
                        justifyContent="space-between"
                        height={`calc(100vh - ${MODAL_TOP_BAR_HEIGHT + BORDER_WIDTH + TOP_AND_BOTTOM_PADDING}px)`}
                    >
                        <Box overflow="auto">
                            <Text variant="h6">{needsTranslations('SimplifiedUsBilling', 'Invoice summary')}</Text>
                            {hasValue(selectedBillingPeriod) &&
                            hasValue(selectedBillingType) &&
                            hasValue(summaryPeriods) ? (
                                <Summary
                                    pricingGroups={summaryPeriods.summaryPricingGroups}
                                    canCalculateInvoiceAmount={canCalculateInvoiceAmount}
                                    showInvoiceSummary={true}
                                    summaryInfoCopy={summaryPeriods.summaryInfoCopy}
                                    summaryPeriods={summaryPeriods.summaryPeriods}
                                    pastSummaryPeriods={[]}
                                    currency={getCurrency()}
                                />
                            ) : null}
                        </Box>
                        <Stack direction="column" spacing={4} alignSelf="flex-end">
                            {childrenErrors.map((error, index) =>
                                selectedChildren.length > 1 ? (
                                    <Notice
                                        key={index}
                                        data-e2e-id={`plan-validation-error-${toLowerCaseWithoutWhitespaces(
                                            error.message,
                                        )}-with-cta`}
                                        severity="warning"
                                        text={error.message}
                                        button={{
                                            text: i18next.t(
                                                'react.simplifiedUsBillingPlan.translations.json.planValidation.removeChildren',
                                            ),
                                            onClick: () => handleRemoveChildrenWithError(error),
                                        }}
                                    />
                                ) : (
                                    <Notice
                                        key={index}
                                        data-e2e-id={`plan-validation-error-${toLowerCaseWithoutWhitespaces(
                                            error.message,
                                        )}-no-cta`}
                                        severity="warning"
                                        text={error.message}
                                    />
                                ),
                            )}
                        </Stack>
                        <Stack direction="column" mt={6} alignItems="flex-end" spacing={4}>
                            <FormControl>
                                <Checkbox
                                    data-e2e-id="add-new-template-checkbox"
                                    data-intercom-target={TARGET_IDS.ADD_PLAN_AS_NEW_TEMPLATE_CHECKBOX}
                                    label={i18next.t(
                                        'react.simplifiedUsBillingPlan.translations.json.templates.addAsNewTemplate',
                                    )}
                                    onChange={e => handleAddNewTemplateChange(e.target.checked)}
                                    checked={shouldAddNewTemplate}
                                />
                            </FormControl>
                            <Stack spacing={2} width="100%">
                                <Button
                                    variant="tertiary"
                                    text={i18next.t('react.simplifiedUsBillingPlan.translations.json.cancel')}
                                    onClick={cancelButton.onCancel}
                                    fullWidth
                                />
                                <Button
                                    isLoading={saveButton.isLoading}
                                    isDisabled={saveButton.isDisabled}
                                    type="submit"
                                    variant="primary"
                                    text={i18next.t('react.simplifiedUsBillingPlan.translations.json.save')}
                                    data-e2e-id="save-button"
                                    fullWidth
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                </Grid>
            </Form>
            {displayedModal.type === 'startPeriod' &&
            selectedBillingType &&
            hasValue(proratedItemsForStartCustomPeriod) &&
            isNonEmpty(proratedItemsForStartCustomPeriod) ? (
                <Suspense fallback={<Spinner centered margined />}>
                    <CustomPeriodModal
                        onClose={handleModalClose}
                        onSave={handleCustomStartPeriodSave}
                        onDateChange={handleCustomStartPeriodChange}
                        onProratedItemChange={handleCustomStartPeriodProratedItemUpdate}
                        customPeriodDates={customPeriodState.customPeriodDates}
                        proratedItems={proratedItemsForStartCustomPeriod}
                    />
                </Suspense>
            ) : null}
            {displayedModal.type === 'endPeriod' &&
            hasValue(proratedItemsForEndCustomPeriod) &&
            isNonEmpty(proratedItemsForEndCustomPeriod) &&
            hasValue(startDateOfEndPeriod) &&
            hasValue(selectedEndDate) ? (
                <Suspense fallback={<Spinner centered margined />}>
                    <EndPeriodModal
                        endDate={selectedEndDate}
                        startDate={startDateOfEndPeriod}
                        onProratedItemChange={handleCustomEndPeriodProratedItemUpdate}
                        onSave={handleCustomEndPeriodSave}
                        onCancel={handleCustomEndPeriodCancel}
                        proratedItems={proratedItemsForEndCustomPeriod}
                    />
                </Suspense>
            ) : null}
        </>
    );
};
